<template>
  <div>
    <Modal v-bind="$attrs" v-on="$listeners" :xIconDisabled="true">
      <div v-if="loading" class="loading-mask">
        Loading...
      </div>

      <template #title>
        <span class="text-base">物件選択</span>
      </template>
      <template #subtitle>
        契約中の物件から選択してください
      </template>

      <div class="content">
        <!-- 検索 -->
        <div class="flex flex-wrap items-end gap-2 pb-3">
          <!-- 物件コード -->
          <div>
            <label class="block text-xs text-gray-500">物件コード</label>
            <input
              type="number"
              placeholder="物件コード"
              onwheel="return false;"
              class="border border-gray-300 rounded-md leading-5 text-sm w-32 focus:text-gray-900"
              :class="filter.siteCode ? 'filled' : ''"
              v-model="filter.siteCode"
              @keyup.enter="getData(1)"
            />
          </div>

          <!-- 物件名 -->
          <div>
            <label class="block text-xs text-gray-500">物件名</label>
            <input
              type="search"
              placeholder="物件名 もしくは カナ"
              class="border border-gray-300 rounded-md leading-5 text-sm w-64 focus:text-gray-900"
              :class="filter.site ? 'filled' : ''"
              v-model="filter.site"
              @keyup.enter="getData(1)"
            />
          </div>

          <!-- 得意先名 -->
          <div>
            <label class="block text-xs text-gray-500">得意先名</label>
            <input
              type="search"
              placeholder="得意先名"
              class="border border-gray-300 rounded-md leading-5 text-sm w-64 focus:text-gray-900"
              :class="filter.client ? 'filled' : ''"
              v-model="filter.client"
              @keyup.enter="getData(1)"
            />
          </div>

          <PrimaryButton text="検索" size="normal" @click="getData(1)" />
        </div>

        <!-- 物件リスト -->
        <div class="bg-white rounded-md border border-gray-200 list-height">
          <table class="text-sm list-table">
            <thead class="sticky top-0 z-20 text-xs text-gray-700">
              <tr class="column">
                <th v-for="(c, i) in column" :key="'col' + i" class="p-2 text-left" :class="c.class">
                  {{ c.name }}
                </th>
              </tr>
            </thead>

            <tbody>
              <tr
                v-for="(row, index) in list"
                :key="'row' + index"
                :class="[selectedIndex === index ? 'bg-selected' : '', isSiteStart(index) ? 'site-start' : '']"
                class="hover-color text-sm text-gray-800 cursor-pointer"
                @click="clickRow(index)"
              >

                <!-- 物件名／物件コード／住所（同じ物件コードは結合するため先頭行のみ描画） -->
                <td v-if="row.rowspan && (index == 0 || list[index - 1].cdSite2 != row.cdSite2)" :rowspan="row.rowspan" class="site-cell p-2 border-r border-gray-200">
                  <div class="font-bold">{{ row.site1 }}</div>
                  <div class="font-bold">{{ row.site2 }}</div>
                  <div class="text-xs text-green-700">{{ row.cdSite2 }}</div>
                  <div class="text-xs text-gray-600">{{ row.siteAddress }}</div>
                </td>

                <!-- 得意先名／支店名／FAX（同上） -->
                <td v-if="row.rowspan && (index == 0 || list[index - 1].cdSite2 != row.cdSite2)" :rowspan="row.rowspan" class="site-cell text-xs p-2 border-r border-gray-200">
                  <div>{{ row.client1 }}</div>
                  <div class="text-xs text-gray-600">{{ row.client2 }}</div>
                  <div v-if="row.fax" class="text-xs text-green-700">FAX {{ row.fax }}</div>
                </td>

                <!-- 選択 -->
                <td class="p-2 text-center align-middle">
                  <input
                    type="radio"
                    class="cursor-pointer"
                    :value="index"
                    :checked="selectedIndex === index"
                    @change="clickRow(index)"
                  />
                </td>

                <!-- 契約コード／営業所コード -->
                <td class="p-2 text-xs whitespace-nowrap text-center">
                  <div class="font-bold">{{ row.cdContract }}</div>
                  <!-- <div class="text-gray-600">営業所 {{ row.cdSalesOffice }}</div> -->
                </td>

                <!-- 契約期間 -->
                <!-- <td class="p-2 text-xs whitespace-nowrap">
                  <div>{{ formatDate(row.startDateContract) }}</div>
                  <div class="text-gray-600">〜 {{ formatDate(row.endDateContract) }}</div>
                </td> -->

                <!-- 作業内容 -->
                <td class="p-2 text-xs whitespace-nowrap">
                  {{ selectionName(workSelections, row.cdContentsWork) }}
                </td>

                <!-- 曜日別（出勤形態／開始〜終了／実働） -->
                <td class="p-1">
                  <div class="flex">
                    <div
                      v-for="w in weekList"
                      :key="'day' + index + w.id"
                      class="day-cell"
                      :class="{ sun: w.id === 'Sunday' }"
                    >
                      <div class="day-name">{{ w.name }}</div>

                      <!-- 勤務なし -->
                      <template v-if="!row['workingHours' + w.id] || !Number(row['workingHours' + w.id])">
                        <div class="text-gray-400">-</div>
                      </template>
                      <!-- 勤務あり -->
                      <template v-else>
                        <div v-if="attendanceText(row, w)" class="attendance-label">{{ attendanceText(row, w) }}</div>
                        <div class="times">{{ formatTime(row['start' + w.id]) }}</div>
                        <div class="times">〜{{ formatTime(row['end' + w.id]) }}</div>
                        <div class="work">{{ row['workingHours' + w.id] || 0 }} h</div>
                      </template>
                    </div>
                  </div>
                </td>

                <!-- 祝日区分 -->
                <td class="p-2 text-xs text-center whitespace-nowrap">
                  {{ selectionName(holidayType, row.kubunHolidayContract) }}
                </td>

                <!-- ごみ搬出 -->
                <td class="p-2 text-xs text-center whitespace-nowrap">
                  {{ flagText(row.hasCarryingoutGarbageContract) }}
                </td>

                <!-- 全社休日出力 -->
                <td class="p-2 text-xs text-center whitespace-nowrap">
                  {{ flagText(row.hasExportingCompanysHolidayContract) }}
                </td>

                <!-- 時間指定 -->
                <td class="p-2 text-xs text-center whitespace-nowrap">
                  {{ flagText(row.specificTime) }}
                </td>
              </tr>

              <tr v-if="!list.length && !loading">
                <td :colspan="column.length" class="p-2 text-center text-gray-400 font-bold py-6">対象データがありません</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- ページネーション -->
        <div class="my-3 w-fit mx-auto">
          <pagination id="pagenation" v-model="page" :per-page="perPage" :records="records" class="text-center" @paginate="getData()" />
        </div>
      </div>

      <div class="text-right text-sm font-bold text-red-700">
        選択した契約情報を反映します（勤務時間も全て）
      </div>

      <template #action>
        <!-- 押し間違いを防ぐため、キャンセルは左端・決定は右端に離して配置する -->
        <div class="flex justify-between w-full p-1">
          <!-- キャンセル -->
          <SecondaryButton
            text="キャンセル"
            size="normal"
            class="border-gray-700 btn-w"
            @click="closed"
          />
          <!-- 決定 -->
          <PrimaryButton
            text="決定"
            size="normal"
            class="btn-w"
            :disabled="!selectedSite"
            :class="{ 'opacity-50 cursor-not-allowed': !selectedSite }"
            @click="decide"
          />
        </div>
      </template>
    </Modal>
  </div>
</template>
<script>

import Modal from '@components/Modal.vue'
import Pagination from 'vue-pagination-2'
import PrimaryButton from '@components/PrimaryButton.vue'
import SecondaryButton from '@components/SecondaryButton.vue'
import * as backend from '@libs/backend'
import * as constantsAKBS from '@libs/constantsAKBS'

export default {
  components: {
    Modal,
    Pagination,
    PrimaryButton,
    SecondaryButton
  },

  props: {
    // 親画面データ
    modalData: {
    },
    open: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      // 物件リスト
      list: [],
      // 検索
      filter: {
        siteCode: '',
        site: '',
        client: ''
      },
      // カラム
      column: [
        { name: '物件名\r\n物件コード\r\n住所' },
        { name: '得意先名\r\n支店名\r\nFAX' },
        { name: '要選択', class: 'text-red-700' },
        { name: '契約コード' },
        // { name: '契約開始日\r\n契約終了日' },
        { name: '作業内容' },
        { name: '曜日別勤務' },
        { name: '祝日区分' },
        { name: 'ごみ搬出' },
        { name: '全社休日\r\n出勤' },
        { name: '時間指定' },
      ],
      // 曜日リスト
      weekList: constantsAKBS.WEEK_LIST,
      // 勤務形態区分
      attendanceList: constantsAKBS.ATTENDANCE,
      // 作業内容
      workSelections: constantsAKBS.WORK_SELECTIONS,
      // 祝日区分
      holidayType: constantsAKBS.HOLIDAYTYPE_SELECTIONS,
      // 選択した行の位置
      selectedIndex: null,
      // ページ数
      page: 1,
      // １ページの最大件数
      perPage: 50,
      // 全件数
      records: 0
    }
  },

  computed: {
    /**
     * 選択した物件
     */
    selectedSite() {
      if (this.selectedIndex === null) {
        return null
      }
      return this.list[this.selectedIndex] || null
    }
  },

  watch: {
    open() {
      if (!this.open) {
        return
      }
      // 既に物件が入力されていれば、その物件名で絞り込んだ状態で開く
      // 物件名は「物件名1＋改行＋物件名2」で持っているため、改行より前の物件名1だけで検索する
      this.filter.site = String((this.modalData && this.modalData.site) || '').split(/\r?\n/)[0]
      this.getData(1)
    }
  },

  methods: {
    /**
     * 物件リスト取得
     * @param num 表示するページ数（指定時はそのページへ移動）
     */
    async getData(num) {
      this.loading = true
      if (num) {
        this.page = num
      }
      // 表示内容が入れ替わるため選択は解除する
      this.selectedIndex = null
      try {
        const res = await backend.searchData('akbsTable/getAkbsSite', {
          perPage: this.perPage,
          page: this.page,
          ...this.filter,
          start: this.modalData.start
        })
        this.list = res.data.data || []
        this.records = this.list.length ? Number(this.list[0].totalSize) : 0
      } catch (error) {
        this.list = []
        this.records = 0
        alert('エラーが発生しました')
      }
      this.loading = false
    },

    /**
     * 行クリックイベント
     * @param index 選択した行の位置
     */
    clickRow(index) {
      this.selectedIndex = index
    },

    /**
     * 物件の先頭行（前の行から物件が変わる）か
     * cdSite は契約詳細の列で、契約詳細が無い物件では null になるため、物件コード（cdSite2）で判定する
     * @param index 判定する行の位置
     */
    isSiteStart(index) {
      return index == 0 || this.list[index - 1].cdSite2 != this.list[index].cdSite2
    },

    /**
     * 出勤形態の表示名
     * コード単体・カンマ区切り・配列のいずれでも表示できるようにしている
     * @param row 契約
     * @param week 曜日
     */
    attendanceText(row, week) {
      const value = row['attendance' + week.id]
      if (value === null || value === undefined || value === '') {
        return ''
      }
      const codes = Array.isArray(value) ? value : String(value).split(',')
      return codes
        .map(code => this.selectionName(this.attendanceList, code && typeof code == 'object' ? code.id : code))
        .filter(name => name)
        .join('・')
    },

    /**
     * 選択肢の表示名取得
     * @param selections 選択肢
     * @param id コード
     */
    selectionName(selections, id) {
      if (id === null || id === undefined || id === '') {
        return ''
      }
      const target = selections.find(s => String(s.id) == String(id).trim())
      return target ? target.name : id
    },

    /**
     * 区分の表示（あり：○／なし：-）
     * @param value 区分
     */
    flagText(value) {
      if (value === 1) return '有り'
      if (value === 0) return '無し'
      return '未設定'
    },

    /**
     * 時刻表示
     * @param value 時刻
     */
    formatTime(value) {
      if (!value && value !== 0) {
        return ''
      }
      const time = String(value)
      // HHmm 形式で返る場合は区切りを入れる
      if (/^\d{3,4}$/.test(time)) {
        return time.padStart(4, '0').replace(/^(\d{2})(\d{2})$/, '$1:$2')
      }
      return time
    },

    /**
     * 決定ボタンイベント
     */
    decide() {
      if (!this.selectedSite) {
        return
      }
      this.$emit('selected', this.selectedSite)
      this.closed()
    },

    /**
     * 初期化して閉じる
     */
    closed() {
      this.loading = false
      this.list = []
      this.filter = {
        siteCode: '',
        site: '',
        client: ''
      }
      this.selectedIndex = null
      this.page = 1
      this.records = 0
      this.$emit('closed')
    }
  }
}

</script>
<style scoped>
/* 表の幅に合わせて広がるが、画面幅を超えたら横スクロールで見せる */
.content {
  width: max-content;
  min-width: 1000px;
  max-width: calc(100vw - 5rem);
  margin: 4px;
}

.list-height {
  height: 60vh;
  overflow-x: auto;
  /* auto にすると、縦スクロールバーの幅ぶんだけ表がはみ出して横スクロールが出るため、
     常時表示にして幅の計算に含める */
  overflow-y: scroll;
}

/* 検索条件が入力されている欄は目立たせる */
.filled {
  font-weight: bold;
  background: #fef9c3;
}

/* キャンセル・決定は同じ幅に揃える */
.btn-w {
  width: 150px;
}

/* 表の中は折り返さない */
.list-table {
  width: max-content;
  min-width: 100%;
  border-collapse: collapse;
}

.list-table td {
  white-space: nowrap;
  /* 行ごとの区切り線 */
  border-bottom: 1px solid rgb(209, 213, 219);
}

/* 物件が変わる境目は太線（結合セルにも引けるよう先頭行の上に引く） */
.list-table tr.site-start td,
.list-table td.site-cell {
  /* border-collapse では隣接セルの罫線と色を取り合うため、box-shadow で線を引く */
  box-shadow: inset 0 2px 0 0 rgb(31, 41, 55);
}

/* 結合している物件名・得意先名のセルは背景を白にする
   background-clip を付けないと、白背景が罫線に重なって太線が細く見える */
.list-table td.site-cell {
  background: #ffffff;
  background-clip: padding-box;
}

/* 見出しは \r\n の位置だけで改行する */
.list-table th {
  white-space: pre-line;
}

.column th {
  background: #edecf7;
}

.day-cell {
  width: 78px;
  padding: 2px;
  border-left: 1px solid rgb(229, 231, 235);
  font-size: 11px;
  text-align: center;
  line-height: 1.3;
}

.day-cell.sun {
  color: rgb(185, 28, 28);
}

.day-name {
  font-weight: bold;
  color: rgb(75, 85, 99);
}

.attendance-label {
  display: inline-block;
  padding: 0 4px;
  border-radius: 3px;
  background: rgb(224, 231, 255);
  color: rgb(55, 48, 163);
  font-weight: bold;
}

.times {
  white-space: nowrap;
  color: rgb(55, 65, 81);
}

.work {
  color: rgb(21, 128, 61);
  font-weight: bold;
}

.bg-selected {
  background: rgba(184, 244, 242, 0.67);
}

.hover-color:hover {
  background: rgba(184, 244, 242, 0.67);
}

#pagenation >>> ul {
  display: flex !important;
  color: rgb(6, 125, 194);
  font-weight: bold !important;
  cursor: pointer;
}

#pagenation >>> ul > li {
  padding: 0 10px;
}

#pagenation >>> ul > .active {
  background: rgb(6, 125, 194);
  color: white;
}

#pagenation >>> p {
  color: gray;
  font-size: small;
}
</style>
